// Differentiator #2 (Grundlage): ein Teil finden ueber das, was man SIEHT (Beschreibung/Merkmale),
// nicht ueber eine Artikelnummer. Die Suche matcht auch Merkmale ("45 mm" findet die Dichtung). Die
// visuelle/Foto-Erkennungsschicht kommt spaeter; das hier ist der Text+Merkmals-Kern darunter.
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/MaterialSearch.jsx). Geaendert wurden NUR
// Styling-Idiom (v4-Token/.gx-field -> isDarkMode-Verzweigung), Theme-/i18n-Hook, i18n-Keys
// (pm.-Praefix) und die Import-/API-Pfade.
//
// MONOLITH-API-KONVENTION: `user` zuerst, Antwort ist direkt der JSON-Body (.items). MaterialSearch
// nutzt das AKTIVE Werk (me.activePlant, fail-closed aus scope.plants) fuer den Bestand.
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Search, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { searchMaterials, materialStock } from '../api';
import { recordRecent } from '../recency';
import RecentMaterialChips from './RecentMaterialChips';
import { Card } from './ui';

function MaterialSearch({ me, user, onBack }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const plant = me.activePlant;
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const timer = useRef();
  // seq-Guard wie SearchBar: nur die Antwort der juengsten Query darf den State setzen.
  const seq = useRef(0);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const mySeq = ++seq.current;
      setLoadError(false);
      // Monolith-API: `user` zuerst, Antwort ist direkt der JSON-Body (.items).
      searchMaterials(user, q.trim(), me.profile.id)
        .then((res) => { if (seq.current === mySeq) setResults(res?.items || []); })
        .catch(() => { if (seq.current === mySeq) { setResults([]); setLoadError(true); } });
    }, 200);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, user, me.profile.id]);

  return (
    <div>
      <button
        onClick={onBack}
        className={`flex items-center gap-1 mb-3 font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
      >
        <ArrowLeft size={18} /> {t('pm.common.back')}
      </button>
      <h2 className={`text-xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.materials.title')}</h2>
      <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('pm.materials.subtitle')}</p>

      {/* Suchfeld — entspricht .gx-field: Glas (dunkel) / klar (hell). */}
      <div className={`flex items-center gap-2 px-4 min-h-[60px] mb-4 rounded-xl border transition-all duration-200 ${isDarkMode
        ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12]'
        : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40'
      }`}>
        <Search className={`shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
        <input
          autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t('pm.materials.placeholder')}
          className={`flex-1 bg-transparent outline-none text-lg py-3 min-w-0 ${isDarkMode ? 'text-white/80' : 'text-slate-900'}`}
        />
      </div>

      {/* Zuletzt verwendete Materialien — Klick setzt die Suche auf die Materialnummer. */}
      {!q.trim() && (
        <div className="mb-4 -mt-2">
          <RecentMaterialChips user={user} max={10} onPick={(m) => setQ(m.materialId)} />
        </div>
      )}

      {loadError ? (
        <p className={`py-6 text-center ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.common.loadError')}</p>
      ) : results === null ? (
        <p className={`py-6 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.common.loading')}</p>
      ) : results.length === 0 ? (
        <p className={`py-6 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.materials.noResults')}</p>
      ) : (
        <div className="space-y-2">
          {/* key enthaelt das Werk: Werkswechsel remountet die Karten, damit lazy geladener Bestand
              nicht vom vorherigen Werk haengen bleibt. */}
          {results.map((m) => <MaterialCard key={`${plant}:${m.materialId}`} material={m} plant={plant} user={user} role={me.profile.id} />)}
        </div>
      )}
    </div>
  );
}

// Material-Karte mit aufklappbarem Bestand. Bestand wird LAZY beim ersten Aufklappen geladen.
function MaterialCard({ material, plant, user, role }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [stock, setStock] = useState(null); // null = noch nicht geladen

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      // Aufklappen = Nutzungssignal: Material in die Zuletzt-verwendet-Liste (fire-and-forget).
      recordRecent(user, 'materials', {
        materialId: material.materialId, description: material.description, baseUnit: material.baseUnit,
      }, 'materialId');
    }
    // Fehlerzustand ist NICHT terminal: erneutes Aufklappen versucht den Fetch erneut.
    if (next && (stock === null || stock === 'error')) {
      setStock(null);
      // Monolith-API: `user` zuerst, Antwort ist direkt der JSON-Body (.items).
      // Fehler -> 'error'-Marker: [] wuerde faelschlich "Nicht auf Lager" anzeigen.
      try {
        const res = await materialStock(user, material.materialId, plant, role);
        setStock(res?.items || []);
      } catch {
        setStock('error');
      }
    }
  };

  return (
    <Card className="space-y-2">
      <button onClick={toggle} className="w-full text-left flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {/* thumbnailRef ist Portal-eigen; echte Bilder kommen mit der Visual-Search-Schicht. */}
          <div className={`rounded-xl p-2.5 shrink-0 ${isDarkMode ? 'bg-white/[0.08] text-white/40' : 'bg-slate-100 text-slate-400'}`}>
            <Package size={24} />
          </div>
          <div className="min-w-0">
            <div className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{material.description}</div>
            <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{material.materialId}</div>
            {material.characteristics?.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {material.characteristics.map((c, i) => (
                  <span key={i} className={`text-xs rounded px-1.5 py-0.5 ${isDarkMode ? 'bg-white/[0.08] text-white/60' : 'bg-slate-100 text-slate-500'}`}>
                    {c.name}: {c.value}{c.unit ? ` ${c.unit}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        {open
          ? <ChevronUp className={`shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          : <ChevronDown className={`shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />}
      </button>

      {open && (
        <div className={`border-t pt-2 ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          {stock === null ? (
            <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.materials.loadingStock')}</p>
          ) : stock === 'error' ? (
            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.common.loadError')}</p>
          ) : stock.every((s) => s.availableQty === 0) ? (
            <p className={`text-sm font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.materials.outOfStock')}</p>
          ) : (
            stock.map((s, i) => (
              <div key={i} className="flex justify-between text-sm py-0.5">
                <span className={isDarkMode ? 'text-white/60' : 'text-slate-500'}>
                  {t('pm.materials.bin')} {s.storageBin} ({s.storageLocation})
                </span>
                <span className={`font-semibold ${s.availableQty === 0
                  ? (isDarkMode ? 'text-red-300' : 'text-red-600')
                  : (isDarkMode ? 'text-emerald-300' : 'text-emerald-700')}`}>
                  {s.availableQty} {s.unit} {t('pm.materials.available')}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
}

export default MaterialSearch;
